import React, { useState } from 'react';
import { partnerApiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ActivationStatus } from '../types/api';
import { Button, Chip } from './ui';
import './ActivationCard.css';

interface ActivationCardProps {
  activation: ActivationStatus;
  /** Открыть форму заведения — сразу на нужном разделе, если он известен. */
  onEdit: (section?: string) => void;
}

const FIELD_LABELS: Record<keyof ActivationStatus['checks'], string> = {
  name: 'название',
  address: 'адрес',
  city: 'город',
  location: 'точка на карте',
  categories: 'категория',
  menu_items: 'позиция в каталоге',
};

/** Раздел формы, где закрывается пункт чек-листа. */
const FIELD_SECTIONS: Partial<Record<keyof ActivationStatus['checks'], string>> = {
  address: 'location',
  location: 'location',
};

/**
 * Запуск заведения.
 *
 * Раньше активацию дёргал observer после каждой правки профиля, и пробный
 * период начинал течь, пока заведение ещё настраивали. Теперь партнёр
 * нажимает кнопку сам — а значит кнопка обязана существовать: без неё
 * заполненный профиль просто никогда не попадёт в каталог.
 */
export const ActivationCard: React.FC<ActivationCardProps> = ({ activation, onEdit }) => {
  const { loadPartnerProfile } = useAuth();
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (activation.is_activated) return null;

  const missing = (Object.keys(activation.checks) as Array<keyof ActivationStatus['checks']>).filter(
    (key) => !activation.checks[key],
  );
  const isReady = activation.requirements_met && missing.length === 0;

  const handleActivate = async () => {
    setIsActivating(true);
    setError(null);

    try {
      await partnerApiService.activate();
      // Профиль перечитываем в любом случае: сервер мог доначислить пробный
      // период и сдвинуть expires_at.
      await loadPartnerProfile();
    } catch (err: any) {
      const code = err.response?.data?.code;

      if (code === 'activation_requirements_not_met') {
        // Данные разъехались с тем, что показано: подтягиваем свежий профиль,
        // чтобы список недостающего перерисовался сам.
        await loadPartnerProfile();
        setError('Часть данных всё ещё не заполнена — проверьте список выше.');
      } else {
        setError(err.response?.data?.message || 'Не удалось запустить заведение. Попробуйте ещё раз.');
      }
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <section className={`ac${isReady ? ' ac--ready' : ''}`} role="status">
      <div className="ac__body">
        {isReady ? (
          <>
            <h2 className="ac__title">Заведение готово к запуску</h2>
            <p className="ac__text">
              После запуска вы появитесь в приложении для клиентов. С этого же момента начнётся пробный период,
              если он вам положен, — поэтому нажимайте, когда всё действительно готово.
            </p>
          </>
        ) : (
          <>
            <h2 className="ac__title">Чтобы появиться в приложении</h2>
            <p className="ac__text">Осталось заполнить — без этого запуск недоступен:</p>
            <ul className="ac__missing">
              {missing.map((key) => (
                <li key={key}>
                  {/* Ключ, которого кабинет ещё не знает, показываем как есть —
                      иначе в списке недостающего была бы пустая плашка. */}
                  <Chip>{FIELD_LABELS[key] ?? key}</Chip>
                </li>
              ))}
            </ul>
          </>
        )}

        {error && <p className="ac__error">{error}</p>}

        <div className="ac__actions">
          {isReady ? (
            <>
              <Button variant="deal" onClick={handleActivate} disabled={isActivating}>
                {isActivating ? 'Запускаем…' : 'Запустить заведение'}
              </Button>
              <Button variant="ghost" onClick={() => onEdit()} disabled={isActivating}>
                Сначала проверю данные
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => onEdit(FIELD_SECTIONS[missing[0]])}>
              Заполнить
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};
